import { TransactionStage } from 'src/generated/graphql.generated';

import { BLUESAGE_REFUND_IN_PROGRESS_REASON, describeBlueSageRefundFailure } from '../bluesage.util';

/** The shape the row menu actually has to reason about. */
interface RefundableTransaction {
    amount?: number | null;
    stage: TransactionStage;
    type?: string | null;
    deletedAt?: number | null;
    metadata?: Record<string, unknown> | null;
}

/**
 * What is still refundable, in minor units.
 *
 * **Read this rather than assuming the full amount.** After a partial refund
 * the original comes back as `partially_refunded`, which the contract is
 * explicit about: *more CAN still be refunded — treat it as a live, refundable
 * sale, not a closed one.* Grouping it with `refunded` would hide money the
 * customer is still owed.
 *
 * BlueSage holds the authoritative remaining figure and refuses an over-refund
 * with `409 refund_exceeds_remaining` carrying it. This is the CRM's own best
 * estimate from what it has recorded, used to pre-fill the dialog — not a
 * second source of truth.
 */
export function refundableMinorOf(transaction: RefundableTransaction): number {
    const original = Math.abs(transaction.amount ?? 0);
    const alreadyRefunded = Number(transaction.metadata?.bluesageRefundedMinor ?? 0);

    // ⚠ `bluesageRefundedMinor` is written by `refundTransaction` after every
    // refund, as the SUM of the live legs. Nothing wrote it until 2026-08-28,
    // so this always fell through to the full original — which is how the
    // dialog offered the whole $1.07 again after $0.57 had gone back.
    return Number.isFinite(alreadyRefunded) && alreadyRefunded > 0
        ? Math.max(0, original - alreadyRefunded)
        : original;
}

/**
 * Why this transaction cannot be refunded, or `undefined` when it can.
 *
 * Requirement: this answers a DIFFERENT question from "may this person refund".
 * No permission hides the item entirely; this disables it with a reason. The
 * strings are what the operator reads in the tooltip, so each says what to do
 * about it rather than merely stating a rule.
 */
export function describeWhyNotRefundable(
    transaction: RefundableTransaction,
    jobInCurrentZone: boolean,
    refundInProgress = false,
): string | undefined {
    if (transaction.deletedAt) {
        return 'This transaction has been deleted.';
    }
    if (!jobInCurrentZone) {
        return 'This job is in another zone — switch to it to refund this.';
    }
    // Requirement: one refund at a time. The server refuses a second while the
    // first is still at the terminal, so the row must not offer it.
    if (refundInProgress) {
        return BLUESAGE_REFUND_IN_PROGRESS_REASON;
    }
    if (transaction.type === 'refund') {
        return 'This is already a refund.';
    }
    if (transaction.stage !== TransactionStage.Paid) {
        return 'Only a paid transaction can be refunded.';
    }
    // ⚠ The presence of `refundedBy` is NOT a reason to refuse.
    //
    // It is written after ANY refund, a partial included, and reading it as
    // "done" is what greyed this item out on a $1.07 sale with $0.50 still
    // refundable — measured live on 2026-08-28. The contract is explicit that
    // `partially_refunded` means *more CAN still be refunded*.
    //
    // The only client-side question worth asking is the amount one, and the
    // server (K28) sums the actual legs rather than trusting this.
    if (refundableMinorOf(transaction) <= 0) {
        return 'This transaction has already been refunded in full.';
    }

    return undefined;
}

/**
 * What the customer's card will actually receive for a partial refund of
 * `baseMinor`.
 *
 * ⚠ **Quoting only.** The figure that reaches the rail is computed SERVER-side
 * by `grossUpRefundToCard` in `transactions.mutations.ts`; this mirrors it so
 * the operator can read the customer's number before pressing Confirm. It lives
 * here rather than inline in the component so the arithmetic is unit-testable —
 * a quote that disagrees with what is taken is worse than no quote.
 *
 * Cumulative, and that is the whole trick: rounding each partial on its own
 * loses the differential entirely on a long series of small refunds, because
 * every one of them rounds to zero. Taking the difference between two points on
 * the same curve makes the series telescope, so however a sale is sliced the
 * card receives exactly `base + differential`.
 *
 * The tip is deliberately NOT included — it is the crew's money, not the
 * customer's to get back on a partial. ⚠ A FULL refund still returns it,
 * because it reverses the entire capture through the void rail. That
 * difference is a live decision for Karve and YMM, which is why the dialog
 * states the figure in both cases rather than hiding it.
 */
export function refundToCardMinor(
    baseMinor: number,
    alreadyRefundedBaseMinor: number,
    saleBaseMinor: number,
    saleDifferentialMinor: number,
    salePaidMinor: number,
): number {
    if (saleBaseMinor <= 0 || saleDifferentialMinor <= 0) { return baseMinor; }

    // The same ceiling the server applies — what the card was actually charged —
    // and the same refusal to gross up at all when nothing records it. Mirrored
    // exactly, because a quote naming a figure the server would then clamp is
    // worse than showing no quote.
    if (salePaidMinor <= saleBaseMinor) { return baseMinor; }

    const cardTotalFor = (base: number) => Math.min(
        salePaidMinor,
        base + Math.round((base * saleDifferentialMinor) / saleBaseMinor),
    );

    return cardTotalFor(alreadyRefundedBaseMinor + baseMinor)
        - cardTotalFor(alreadyRefundedBaseMinor);
}

/**
 * What a SENT refund has come to, read off its leg.
 *
 * Requirement: a BlueSage refund answers the browser as soon as its leg exists
 * and settles in the background — a terminal can take minutes — so the dialog
 * reports what the LEG says and never what the request implied.
 *
 *  - `waiting`     the leg is still `pending`, or has not reached this browser yet
 *  - `confirmed`   BlueSage settled it
 *  - `refused`     BlueSage said no and nothing moved — the operator may try again
 *  - `unconfirmed` the outcome is unknown; trying again could refund twice
 *  - `overdue`     no answer within the window — stop spinning and send them to the row
 */
export type BlueSageRefundOutcome =
    | { state: 'waiting' | 'confirmed' | 'overdue' }
    | { state: 'refused' | 'unconfirmed'; message: string };

export function describeBlueSageRefundOutcome(input: {
    done: boolean;
    awaitsConfirmation: boolean;
    overdue: boolean;
    leg: { stage?: string | null; metadata?: Record<string, unknown> | null } | undefined;
}): BlueSageRefundOutcome | undefined {
    const { done, awaitsConfirmation, overdue, leg } = input;

    // Not sent yet, or a payment taken elsewhere: that refund is only a record.
    if (!done || !awaitsConfirmation) { return undefined; }

    if (!leg || leg.stage === 'pending') {
        return { state: overdue ? 'overdue' : 'waiting' };
    }
    if (leg.stage === 'paid') { return { state: 'confirmed' }; }

    return {
        state: leg.metadata?.bluesageRefundUnconfirmed === true ? 'unconfirmed' : 'refused',
        message: describeBlueSageRefundFailure(leg)
            ?? 'BlueSage did not complete this refund. Check the row on the job.',
    };
}
